import { useMemo, useState } from "react";
import {
  Box,
  OptionPanelRow,
  OptionPanelSection,
  Switch,
  Text,
  toastManager,
} from "@salestrak/ui";

import { EntityTag } from "#app/components/entity";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { ViewEvent } from "#app/lib/analytics";
import { useViews } from "#app/lib/views/view-context";

/**
 * The display-options panel's Workflows page: the workflows a WORKSPACE
 * dashboard reads from — the one place the source set changes. Every widget
 * re-scopes to it and no widget overrides it.
 *
 * A page in the panel's own grammar: one row per workflow, a switch on each,
 * the same list the viewer drilled in from. The set is a column on the view
 * row (`workflowIds`), not a display option — it decides what the board is
 * OF, so a flip commits at once like Rename does and never counts toward
 * Save. Turning off the last workflow is refused with a toast: a dashboard
 * with no source has nothing to show. An archived workflow is offered only
 * while it is still in the set, so it can be removed but never added; one
 * the viewer cannot see never appears, and the server already scopes their
 * data to what they can reach.
 */
export function ViewWorkflows() {
  const { activeView, canManage, updateView, trackView } = useViews();
  const { workflows } = useWorkspaceData();
  const [saving, setSaving] = useState(false);

  const workflowIds = activeView?.workflowIds;
  // One stable array per view row: a fresh `[]` fallback inside the memo
  // dependencies would re-filter on every render.
  const selected = useMemo(() => workflowIds ?? [], [workflowIds]);
  const rows = useMemo(
    () =>
      workflows.filter(
        (workflow) => !workflow.archived || selected.includes(workflow.workflowId),
      ),
    [workflows, selected],
  );

  if (!activeView) return null;

  const toggle = (workflowId: string, on: boolean) => {
    const next = on
      ? [...selected, workflowId]
      : selected.filter((id) => id !== workflowId);
    if (next.length === 0) {
      toastManager.add({
        title: "A dashboard needs at least one workflow",
        type: "error",
      });
      return;
    }
    setSaving(true);
    void updateView(activeView.id, { workflowIds: next })
      .then((saved) => {
        if (saved) {
          trackView(ViewEvent.UpdateWorkflows, saved, {
            workflowCount: next.length,
            on,
          });
        }
      })
      .finally(() => setSaving(false));
  };

  return (
    <Box direction="column" gap="sm">
      <Box paddingX="sm">
        <Text size="sm" variant="muted">
          Every widget on this dashboard reads from these workflows.
        </Text>
      </Box>
      <OptionPanelSection>
        {rows.map((workflow) => {
          const on = selected.includes(workflow.workflowId);
          return (
            <OptionPanelRow
              key={workflow.workflowId}
              label={
                <EntityTag
                  entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
                  interactive={false}
                  ghost={false}
                />
              }
              disabled={!canManage}
            >
              <Switch
                checked={on}
                disabled={!canManage || saving}
                onCheckedChange={(checked) => toggle(workflow.workflowId, !!checked)}
              />
            </OptionPanelRow>
          );
        })}
      </OptionPanelSection>
    </Box>
  );
}
